import { useEffect, useRef } from "react";
import { Selections } from "../../../services/api";
import { resizedImageUrl } from "../../../hooks/useStorageUrl";

// Size of the cover image the admin panel generates and saves to Supabase (selections.cover_url).
// Big enough for a full-width cover on a phone at 3x.
export const SAVED_COVER_SIZE = 1200;

// Most track covers the cover shows (a selection has up to 10 tracks).
export const MAX_COVER_TRACKS = 10;

// ─── Layout ───────────────────────────────────────────────────────────────────
// Records dumped on a table: 10 sleeves at random angles, two vinyls on top, and the first
// track's sleeve biggest and on top of everything. The randomness is seeded by the selection id,
// so a selection always gets the same arrangement.
// Keep in sync with mobile/src/lib/selectionCoverLayout.ts (same numbers, same order of random calls).
// Every value is a fraction of the cover size; rotation is in degrees.
export interface SleeveLayout { cover: number; x: number; y: number; w: number; rotation: number }
export interface VinylLayout { label: number; x: number; y: number; w: number }

const SLEEVES = 10;
const MAX_TILT = 22;

const hashSeed = (seed: string) => {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) hash = Math.imul(hash ^ seed.charCodeAt(i), 16777619);
  return hash >>> 0;
};

export function selectionCoverLayout(seed: string, coverCount: number) {
  let state = hashSeed(seed);
  const random = () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };

  // A 4×3 grid of spots, shuffled, so the sleeves spread over the whole table.
  const spots: [number, number][] = [];
  for (let row = 0; row < 3; row++) for (let col = 0; col < 4; col++) spots.push([col, row]);
  for (let i = spots.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [spots[i], spots[j]] = [spots[j], spots[i]];
  }

  // With fewer than 10 tracks the covers repeat, so the table always looks full.
  const sleeves: SleeveLayout[] = [];
  for (let i = 0; i < SLEEVES; i++) {
    const [col, row] = spots[i];
    const lead = i === 0;
    const w = lead ? 0.44 : 0.3 + random() * 0.1;
    // The first track stays near the middle; the others land anywhere, some off the edges.
    const x = lead ? 0.26 + random() * 0.08 : -0.06 + col * 0.27 + random() * 0.12;
    const y = lead ? 0.2 + random() * 0.1 : -0.06 + row * 0.36 + random() * 0.12;
    sleeves.push({ cover: i % coverCount, x, y, w, rotation: (random() - 0.5) * 2 * MAX_TILT });
  }

  // One vinyl bottom-left, one top-right; their labels are tracks 2 and 3.
  const vinyls: VinylLayout[] = [0, 1].map((i) => ({
    label: (i + 1) % coverCount,
    w: 0.38 + random() * 0.1,
    x: i ? 0.5 + random() * 0.2 : -0.08 + random() * 0.18,
    y: i ? -0.1 + random() * 0.15 : 0.48 + random() * 0.2,
  }));

  return { sleeves, vinyls };
}

// ─── Drawing ──────────────────────────────────────────────────────────────────
// Used live (fallback) and by the admin panel to make the saved image.
const COLORS = { table: "#FFF2A3", sleeveEdge: "#f5f1e8", vinyl: "#0A1111", groove: "rgba(255,255,255,0.07)" };

export async function drawSelectionCover(
  canvas: HTMLCanvasElement,
  coverPaths: string[],
  size: number,
  pixelRatio = 1,
  seed = "",
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  canvas.width = size * pixelRatio;
  canvas.height = size * pixelRatio;
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

  const loadImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

  // Small resized copies (the biggest sleeve is ~45% of `size`), not the full-size files.
  const getPublicUrl = (path: string) =>
    resizedImageUrl("covers", path, Math.round(size * 0.5 * Math.min(pixelRatio, 2))) ?? path;

  const paths = coverPaths.slice(0, MAX_COVER_TRACKS);
  const results = await Promise.allSettled(paths.map((path) => loadImage(getPublicUrl(path))));
  const images = results.map((r) => (r.status === "fulfilled" ? r.value : null));
  const { sleeves, vinyls } = selectionCoverLayout(seed, paths.length);
  const edge = size * 0.012;

  const drawSleeve = ({ cover, x, y, w, rotation }: SleeveLayout) => {
    const img = images[cover];
    if (!img) return;
    const side = w * size;
    ctx.save();
    ctx.translate((x + w / 2) * size, (y + w / 2) * size);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.fillStyle = COLORS.sleeveEdge;
    ctx.fillRect(-side / 2 - edge, -side / 2 - edge, side + edge * 2, side + edge * 2);
    ctx.drawImage(img, -side / 2, -side / 2, side, side);
    ctx.restore();
  };

  const drawVinyl = ({ label, x, y, w }: VinylLayout) => {
    const radius = (w * size) / 2;
    const cx = x * size + radius;
    const cy = y * size + radius;
    const circle = (r: number) => { ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); };
    ctx.fillStyle = COLORS.vinyl;
    circle(radius);
    ctx.fill();
    ctx.strokeStyle = COLORS.groove;
    ctx.lineWidth = Math.max(1, size / 400);
    [0.82, 0.64, 0.48].forEach((f) => { circle(radius * f); ctx.stroke(); });
    const img = images[label];
    if (img) {
      ctx.save();
      circle(radius * 0.34);
      ctx.clip();
      ctx.drawImage(img, cx - radius * 0.34, cy - radius * 0.34, radius * 0.68, radius * 0.68);
      ctx.restore();
    }
    ctx.fillStyle = COLORS.vinyl;
    circle(radius * 0.03);
    ctx.fill();
  };

  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = COLORS.table;
  ctx.fillRect(0, 0, size, size);

  // Back to front: the other sleeves, the vinyls, then the first track's sleeve on top.
  sleeves.slice(1).reverse().forEach(drawSleeve);
  vinyls.forEach(drawVinyl);
  drawSleeve(sleeves[0]);
}

// The cover as an image file, ready to upload (admin panel). Null when there are no track covers.
// JPEG: the cover is a full yellow square (no transparency), so it is ~10x smaller than PNG.
export async function renderSelectionCoverFile(coverPaths: string[], seed: string): Promise<File | null> {
  if (!coverPaths.length) return null;
  const canvas = document.createElement("canvas");
  await drawSelectionCover(canvas, coverPaths, SAVED_COVER_SIZE, 1, seed);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
  return blob ? new File([blob], "selection-cover.jpg", { type: "image/jpeg" }) : null;
}

// ─── SelectionCover ───────────────────────────────────────────────────────────
interface SelectionCoverProps {
  coverUrls: (string | null | undefined)[];
  /** The image saved in Supabase (selections.cover_url). Drawn live from coverUrls when missing. */
  savedCover?: string | null;
  /** Pixel size; with `fluid`, the largest size it is shown at (for drawing and image resolution). */
  size?: number;
  /** Fill the parent's width (a square), e.g. grid tiles and the large "This week" cover. */
  fluid?: boolean;
  /** The selection id: picks how the sleeves are scattered, the same every time. */
  seed?: string;
}

export const SelectionCover = ({ coverUrls, savedCover, size = 80, fluid = false, seed = "" }: SelectionCoverProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const validUrls = coverUrls.filter(Boolean).slice(0, MAX_COVER_TRACKS) as string[];
  // A string, so the cover is only redrawn when the actual images change (not on every new array).
  const urlsKey = validUrls.join(",");

  useEffect(() => {
    if (savedCover) return;
    const validUrls = urlsKey ? urlsKey.split(",") : [];
    const canvas = canvasRef.current;
    if (!canvas || validUrls.length === 0) return;
    drawSelectionCover(canvas, validUrls, size, window.devicePixelRatio || 1, seed);
  }, [urlsKey, size, savedCover, seed]);

  const box = fluid
    ? { width: "100%", height: "auto", aspectRatio: "1 / 1" }
    : { width: size, height: size };

  if (savedCover) {
    return (
      <img
        src={resizedImageUrl("covers", savedCover, Math.round(size * 2)) ?? undefined}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        style={{ ...box, display: "block", flexShrink: 0, borderRadius: "8px", objectFit: "cover" }}
      />
    );
  }

  if (validUrls.length === 0) {
    return <div style={{ ...box, background: "#1a1a1a", borderRadius: fluid ? "8px" : "50%", flexShrink: 0 }} />;
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ ...box, display: "block", flexShrink: 0, borderRadius: "8px" }}
    />
  );
};

// ─── Helper ───────────────────────────────────────────────────────────────────
export const getSelectionCovers = (selection: Selections) =>
  selection.selection_tracks?.slice(0, MAX_COVER_TRACKS).map((st) => st.tracks?.cover_url ?? null) ?? [];
